/* eslint-disable max-len */
import {
  Component,
  Element,
  Event,
  EventEmitter,
  Host,
  Listen,
  Method,
  Prop,
  State,
  Watch,
  h,
} from '@stencil/core';
import Choices, { Item } from 'choices.js';
import { versionComponent } from '../../global/inject-dependencies';

interface UiSelectCheckboxChangeEvent {
  checked?: boolean;
  value: string;
}

interface Choice {
  value: string;
  label: string;
  selected?: boolean;
  disabled?: boolean;
}
/**
 * The <select> component is used to create a drop-down list. It is most often used in a form, to collect user input.
 */
@Component({
  tag: 'ui-select',
  styleUrl: 'ui-select.scss',
  shadow: false,
})
export class UiSelect {
  @Element() el: HTMLElement;

  /**
   * The id attribute is needed to associate the drop-down list with a label.
   */
  @Prop() id: HTMLElement['id'];

  /**
   * the value prop reflects the selected values
   */
  @Prop({ mutable: true }) value: string | string[] = '';

  /**
   * Defines the select component's title
   */
  @Prop() label: string;

  /**
   * When set to true, requires to user to choose an option.
   */
  @Prop() required: boolean;

  /**
   * When set to true, makes the whole component disabled
   */
  @Prop() disabled: boolean;

  /**
   * When set to true, allows for the user to choose multiple options simultaneously.
   */
  @Prop() multiple: boolean;

  /**
   * The name attribute is needed to reference the form data after the form is submitted (if you omit the name attribute, no data from the drop-down list will be submitted).
   */
  @Prop() name: string;

  /**
   * Checks whether the component is being used. The default setting is false.
   */
  @Prop() inFocus = false;

  @Prop() message: string;

  /**
   * Gives the user a clue regarding the options available to him or the purpose of the component.
   */
  @Prop() hint: string;

  /**
   * Checks whether the user's input was invalid. Changes the component's appearance when true.
   */
  @Prop() error: boolean;

  /**
   * Checks whether the user's input was valid. Changes the component's appearance when true.
   */
  @Prop() success: boolean;

  /**
   * variant 'checkbox' currently only works if multiple=true
   */
  @Prop() variant: 'default' | 'checkbox' = 'default';

  /**
   * represent the input <options> that should be dynamically disabled. Used for ui-funds-selection.
   */
  @Prop() disabledOptions = [];

  /**
   * Choices array to set choices using setChoices method in Choices.js
   */
  @Prop() choicesArray?: Choice[];

  @State() internalDisabled = false;

  selectElement: HTMLSelectElement;

  choicesInstance: Choices;

  /**
   * Is triggered when an item is selected / deselected. Only applys for variant checkbox.
   */
  @Event({ eventName: 'uiSelectCheckboxChange' })
  selectCheckboxChangeEvent: EventEmitter<UiSelectCheckboxChangeEvent>;

  /**
   * Native `change` event. Read `event.detail.value` to get the changed value.
   */
  @Event({ eventName: 'change' }) change: EventEmitter<CustomEvent>;

  @Listen('showDropdown')
  onDropdownShow() {
    if (this.isVariantCheckbox()) {
      this.choicesInstance.containerOuter.element.addEventListener(
        'mousedown',
        this.onActiveDropdownClick
      );
    }
  }

  @Listen('hideDropdown')
  onDropdownHide() {
    if (this.isVariantCheckbox()) {
      this.choicesInstance.containerOuter.element.removeEventListener(
        'mousedown',
        this.onActiveDropdownClick
      );
    }
  }

  @Listen('change')
  onItemChange(event: CustomEvent) {
    if (this.isVariantCheckbox()) {
      this.setPlaceholderText();
      this.selectCheckboxChangeEvent.emit({ value: event.detail.value });
    }
  }

  /**
   * This will set choices using setChoices() method, when a choices array is passed as a prop
   * Used to update select options properly when dynamically updating multidimensional arrays
   */
  @Watch('choicesArray')
  setChoicesHandler() {
    this.choicesInstance.clearStore();

    this.choicesInstance.setChoices(this.choicesArray, 'value', 'label', false);
  }

  @Listen('choice')
  removeAlreadySelected(event: CustomEvent) {
    if (this.isVariantCheckbox()) {
      if (event.detail.choice.selected) {
        setTimeout(() => {
          this.choicesInstance.removeActiveItemsByValue(
            event.detail.choice.value
          );
          this.choicesInstance._triggerChange(event.detail.choice.value);
        }, 100);
      }
    }
  }

  @Listen('click')
  handleClick() {
    // https://jira.apps.union-investment.de/browse/PB2B-3180
    // Workaround for failing to open the dropdown on click
    // when placed inside a webcomponent with (open) shadowDOM
    this.choicesInstance?.showDropdown();
  }

  @Watch('disabled')
  disabledChangeHandler(newValue: boolean) {
    if (newValue) {
      this.choicesInstance.disable();
    } else {
      this.choicesInstance.enable();
    }
    this.internalDisabled = newValue;
  }

  @Watch('disabledOptions')
  disableItemsChangeHandler(newValue: [], oldValue: []) {
    if (this.isVariantCheckbox()) {
      // check if disableItems prop changed to prevent event loop
      // simple stringify check is enough because of non nested elements + order won't change
      if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
        this.updateDisabledItems();
      }
    }
  }

  isVariantCheckbox() {
    return this.variant === 'checkbox' && this.multiple;
  }

  updateDisabledItems() {
    const newChoicesState = this.choicesInstance._currentState.choices?.map(
      (elem, index) => {
        const copyElem = { ...elem };
        if (index > 0) {
          // check is specific for ui-funds-selection component
          if (this.disabledOptions.includes(JSON.parse(elem.value).value)) {
            copyElem.disabled = true;
            copyElem.selected = false;
          } else {
            copyElem.disabled = false;
          }
        }
        return copyElem;
      }
    );
    // when using setChoices(), selcted items get added twice
    // see choices bug: https://github.com/Choices-js/Choices/issues/855
    this.choicesInstance.setChoices(newChoicesState, 'value', 'label', true);
  }

  hasDuplicates(): boolean {
    const values = (this.choicesInstance.getValue() as Item[]).map(
      (elem) => elem.choiceId
    );
    return new Set(values).size !== values.length;
  }

  /**
   * This will clear the input field and reset the value.
   */
  @Method()
  async clear() {
    this.choicesInstance.setChoiceByValue('');
  }

  componentDidLoad() {
    /*
      This workaround is needed since choices.js disables all options by default when
      the select is disabled and does not enable them again when enabling the select.
      With the workaround the select is always initially enabled
      and then manually set to disabled. Thus the options are not disabled.
    */
    setTimeout(() => {
      this.internalDisabled = this.disabled;
      if (this.disabled) {
        this.choicesInstance.disable();
      } else {
        this.choicesInstance.enable();
      }
    }, 1);

    switch (this.variant) {
      case 'checkbox':
        this.choicesInstance = new Choices(this.selectElement, {
          searchEnabled: false,
          itemSelectText: '',
          shouldSort: false,
          renderSelectedChoices: 'always',
          duplicateItemsAllowed: false,
          removeItems: false,
          allowHTML: false,
          /* eslint-disable */
          callbackOnCreateTemplates(template) {
            return {
              input: ({ classNames }, placeholderValue) =>
                template(`
                <button ${this.containerOuter.isDisabled ? 'disabled' : ''} class="${classNames.input} ${classNames.inputCloned}">${placeholderValue}</button>
              `),
              choice: ({ classNames }, data) =>
                template(`
                <div id=${data.elementId}
                  class="${classNames.item} ${classNames.itemChoice} ${data.disabled ? classNames.itemDisabled : classNames.itemSelectable}"
                  data-select-text="${this.config.itemSelectText}"
                  data-choice
                  ${data.disabled ? 'data-choice-disabled aria-disabled="true"' : 'data-choice-selectable'}
                  data-id="${data.id}"
                  data-value="${data.value}"
                  ${data.groupId > 0 ? 'role="treeitem"' : 'role="option"'}
                >
                  <ui-input ${data.disabled ? 'disabled' : ''} ${data.selected ? 'checked' : ''} type="checkbox"></ui-input>
                  ${data.label}
                </div>
              `),
            };
          },
          /* eslint-enable */
        });
        this.setPlaceholderText();
        break;
      default:
        this.choicesInstance = new Choices(this.selectElement, {
          searchEnabled: false,
          itemSelectText: '',
          removeItemButton: this.multiple,
          shouldSort: false,
          allowHTML: false,
        });
        break;
    }

    if (this.choicesArray) this.setChoicesHandler();

    if (this.inFocus) {
      this.el.focus();
    }
  }

  onActiveDropdownClick = (e) => {
    if (
      e.target === this.choicesInstance.input.element ||
      e.target === this.choicesInstance.containerInner.element
    ) {
      /* workaround to stop dropdown from opening again: mousedown -> open -> timeout -> close */
      setTimeout(() => {
        this.choicesInstance.hideDropdown();
      }, 300);
    }
  };

  setPlaceholderText() {
    const elem: HTMLElement = this.choicesInstance.input.element;
    const selectedItems = (this.choicesInstance.getValue(true) as Item[])
      .length;
    const number =
      selectedItems === undefined || selectedItems === 0
        ? ''
        : ` (${selectedItems})`;
    elem.textContent = `${this.choicesInstance.config.choices[0].label} ${number}`;
  }

  render() {
    return (
      <Host>
        <div
          class={{
            select: true,
            'select--disabled': this.internalDisabled,
            'select--error': this.error,
            'select--success': this.success,
            [`select--${this.variant}`]: true,
          }}
        >
          {this.label && (
            <label class="select__label" htmlFor={this.name}>
              {this.label}
              {this.required && <span class="required">*</span>}
            </label>
          )}
          <select
            class="select__dropdown"
            id={this.id}
            required={this.required}
            multiple={this.multiple}
            name={this.name}
            ref={(el) => {
              this.selectElement = el;
            }}
            disabled={this.internalDisabled}
          >
            <slot />
          </select>
          {this.hint && <div class="select__hint">{this.hint}</div>}
          {this.message && <div class="select__message">{this.message}</div>}
        </div>
      </Host>
    );
  }
}

versionComponent(UiSelect);
